import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faBan, faLayerGroup, faFilter, faListUl } from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { SkeletonCard } from '../components/common/Skeleton';
import { Modal, ConfirmModal } from '../components/common/Modal';
import { FormField } from '../components/common/FormField';
import { EmptyState } from '../components/common/EmptyState';
import { useToast } from '../hooks/useToast';
import { templates as templatesApi } from '../api';
import type { Template } from '../types';

const TYPE_COLORS: Record<string, string> = { '신규': 'var(--color-accent)', '갱신': 'var(--color-success)', '파트너': 'var(--color-warning)' };

export function Templates() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditing] = useState<Template | null>(null);
  const [deactivateId, setDeactivateId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', type: '신규' as '신규' | '갱신' | '파트너', description: '', sections_json: '' });
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const load = useCallback(async () => {
    try { setTemplates(await templatesApi.list()); }
    catch { addToast('error', '템플릿을 불러올 수 없습니다'); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? templates : templates.filter(t => t.type === filter);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: form.name,
        type: form.type,
        description: form.description,
        sections_json: form.sections_json || '[]',
      };
      if (editingTemplate) {
        await templatesApi.update(editingTemplate.id, data);
        addToast('success', '템플릿이 수정되었습니다');
      } else {
        await templatesApi.create(data);
        addToast('success', '템플릿이 생성되었습니다');
      }
      setShowCreate(false);
      setEditing(null);
      setForm({ name: '', type: '신규' as const, description: '', sections_json: '' });
      load();
    } catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!deactivateId) return;
    try { await templatesApi.remove(deactivateId); addToast('success', '템플릿이 비활성화되었습니다'); load(); }
    catch { addToast('error', '비활성화 실패'); }
    setDeactivateId(null);
  };

  const openEdit = (t: Template) => {
    setEditing(t);
    setForm({ name: t.name, type: t.type, description: t.description, sections_json: t.sections_json });
    setShowCreate(true);
  };

  return (
    <main id="main-content" role="main" className="container" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      <FadeUp>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-8)', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>
              <FontAwesomeIcon icon={faLayerGroup} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-3)' }} />
              템플릿
            </h1>
            <p style={{ color: 'var(--color-text-secondary)' }}>딜 MAP 템플릿을 관리하세요</p>
          </div>
          <Button variant="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => { setEditing(null); setForm({ name: '', type: '신규' as const, description: '', sections_json: '' }); setShowCreate(true); }}>
            새 템플릿
          </Button>
        </div>
      </FadeUp>

      <FadeUp delay={0.1}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
          <FontAwesomeIcon icon={faFilter} style={{ color: 'var(--color-text-tertiary)', alignSelf: 'center', marginRight: 'var(--space-2)' }} />
          {['all', '신규', '갱신', '파트너'].map(type => (
            <button key={type} onClick={() => setFilter(type)} aria-pressed={filter === type} style={{
              padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-full)',
              border: '1px solid', borderColor: filter === type ? 'var(--color-accent)' : 'var(--color-border)',
              background: filter === type ? 'var(--color-accent-subtle)' : 'transparent',
              color: filter === type ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              fontSize: 'var(--text-sm)', fontWeight: 500, cursor: 'pointer',
            }}>
              {type === 'all' ? '전체' : type}
            </button>
          ))}
        </div>
      </FadeUp>

      <div className="template-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)' }}>
        {loading ? Array.from({ length: 6 }, (_, i) => <FadeUp key={i} delay={i * 0.05}><SkeletonCard /></FadeUp>) :
         filtered.length === 0 ? (
          <EmptyState icon={faLayerGroup} title="템플릿이 없습니다" description="딜 워크플로우를 표준화할 템플릿을 생성하세요" action={
            <Button variant="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowCreate(true)}>템플릿 생성</Button>
          } />
        ) : filtered.map((template, i) => {
          let sections: string[] = [];
          try { sections = JSON.parse(template.sections_json); } catch {}

          return (
            <FadeUp key={template.id} delay={i * 0.05}>
              <GlassCard as="article">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                      <Badge color={TYPE_COLORS[template.type]}>{template.type}</Badge>
                      {template.is_custom === 1 && <Badge color="var(--color-accent)">커스텀</Badge>}
                    </div>
                    <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>{template.name}</h3>
                    {template.description && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-3)' }}>{template.description}</p>}
                    {sections.length > 0 && (
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <FontAwesomeIcon icon={faListUl} style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)', marginTop: '2px' }} />
                        {sections.map(s => (
                          <span key={s} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', padding: 'var(--space-1) var(--space-2)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)' }}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(template)} aria-label={`${template.name} 편집`}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeactivateId(template.id)} aria-label={`${template.name} 비활성화`}>
                      <FontAwesomeIcon icon={faBan} />
                    </Button>
                  </div>
                </div>
              </GlassCard>
            </FadeUp>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setEditing(null); }} title={editingTemplate ? '템플릿 편집' : '새 템플릿'} footer={
        <><Button variant="ghost" onClick={() => { setShowCreate(false); setEditing(null); }}>취소</Button><Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button></>
      }>
        <form onSubmit={handleSave}>
          <FormField label="이름" required value={form.name} onChange={e => setForm(p => ({ ...p, name: (e.target as HTMLInputElement).value }))} />
          <FormField label="유형" as="select" required value={form.type} onChange={e => setForm(p => ({ ...p, type: (e.target as HTMLSelectElement).value as '신규' | '갱신' | '파트너' }))}>
            <option value="신규">신규</option><option value="갱신">갱신</option><option value="파트너">파트너</option>
          </FormField>
          <FormField label="설명" as="textarea" value={form.description} onChange={e => setForm(p => ({ ...p, description: (e.target as HTMLTextAreaElement).value }))} />
          <FormField label="섹션 (JSON 배열)" as="textarea" value={form.sections_json} onChange={e => setForm(p => ({ ...p, sections_json: (e.target as HTMLTextAreaElement).value }))} placeholder='["Discovery", "Proposal", "Close"]' />
        </form>
      </Modal>

      <ConfirmModal open={deactivateId !== null} onClose={() => setDeactivateId(null)} onConfirm={handleDeactivate} title="템플릿 비활성화" message="이 템플릿은 새 딜에서 더 이상 사용할 수 없습니다. 기존 딜에는 영향이 없습니다." confirmLabel="비활성화" danger />

      <style>{`
        @media (min-width: 768px) { .template-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (min-width: 1024px) { .template-grid { grid-template-columns: repeat(3, 1fr) !important; } }
      `}</style>
    </main>
  );
}
